import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme } from 'antd';
import enUS from 'antd/locale/en_US';
import jaJP from 'antd/locale/ja_JP';
import { useTranslation } from 'react-i18next';

// i18n setup
import './i18n';

// Styles
import './styles/standardLayout.css';

// Components
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import History from './components/History/History';
import LoadingSpinner from './components/Common/LoadingSpinner';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Lazy loaded components
const AzureSearchExplorer = lazy(() => import('./components/Azure/AzureSearchExplorer'));
const ChunkManagement = lazy(() => import('./components/Chunk/ChunkManagement'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const DocumentReview = lazy(() => import('./components/Document/DocumentReview'));
const DocumentReviewDetail = lazy(() => import('./components/Document/DocumentReviewDetail'));

// Types
interface User {
  id: string;
  username: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
}

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route 
            path="/login" 
            element={!user ? <Login /> : <Navigate to="/" replace />} 
          />
          
          {/* Protected routes */}
          <Route
            path="/*"
            element={
              user ? (
                <Layout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/review" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <DocumentReview />
                      </Suspense>
                    } />
                    <Route path="/review/:reviewId" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <DocumentReviewDetail />
                      </Suspense>
                    } />
                    <Route path="/history" element={<History />} />
                    <Route path="/azure-search" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <AzureSearchExplorer />
                      </Suspense>
                    } />
                    <Route path="/chunks" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <ChunkManagement />
                      </Suspense>
                    } />
                    <Route path="/chunks/:documentId" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <ChunkManagement />
                      </Suspense>
                    } />
                    <Route path="/chat" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <ChatPage />
                      </Suspense>
                    } />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </div>
    </Router>
  );
};

const AppContent: React.FC = () => {
  const { i18n } = useTranslation();
  
  const antdLocales = {
    en: enUS,
    ja: jaJP,
  };
  
  const getCurrentAntdLocale = () => {
    return antdLocales[i18n.language as keyof typeof antdLocales] || antdLocales.en;
  };

  return (
    <ConfigProvider
      locale={getCurrentAntdLocale()}
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#1890ff',
          colorSuccess: '#52c41a',
          colorWarning: '#faad14',
          colorError: '#f5222d',
          colorInfo: '#1890ff',
          borderRadius: 6,
        },
      }}
    >
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return <AppContent />;
};

export default App;
